/**
 * ═══════════════════════════════════════════════════════════════════════
 * ИМЯ ЭКРАНА — ОДНО НА ВЕСЬ КАБИНЕТ, И ОНО ЖИВЁТ ЗДЕСЬ.
 *
 * Эталон подписывает каждый из тринадцати экранов кабинета узлом
 * `экран-<имя>` и `data-screen-label` на нём. По этим именам идёт
 * сверка: нет имени — нет сравнения, и уехавший экран уезжает молча.
 *
 * ПОЧЕМУ ОДНА ФУНКЦИЯ, А НЕ АТРИБУТ В КАЖДОЙ СТРАНИЦЕ.
 *
 * Половина страниц кабинета начинается с раннего возврата: «читаю
 * тариф…», «нет доступа», «в адресе нет ключа». Поставь имя на корень
 * готовой разметки — и на этих трёх состояниях узла не будет вовсе,
 * а замер скажет «экрана нет», хотя человек его видит. Имя должно
 * стоять на том, что рисуется ВСЕГДА, — то есть на рамке, а не
 * на содержимом.
 *
 * Отсюда: имя вычисляется из адреса и вешается на `<main>` общей
 * рамки. Страницы, у которых рамки нет (вход, приглашение, сборка,
 * «не найдено»), берут его тем же вызовом.
 *
 * СПИСОК ЗАКРЫТ И СВЕРЯЕТСЯ С ЭТАЛОНОМ. Тринадцать подписей
 * `data-screen-label` в `design/Bidra-Kabinet.dc.html` — ровно
 * тринадцать строк здесь; сторож `screen.spec.ts` держит это
 * равенство. Появится в эталоне четырнадцатый экран — покраснеет.
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * Подпись эталона → имя узла. Подпись — то, что стоит
 * в `data-screen-label`; имя — то, что стоит в `data-d`.
 */
export const ЭКРАНЫ = Object.freeze({
  Биддер: 'экран-биддер',
  Кампания: 'экран-кампания',
  Запросы: 'экран-запросы',
  Намерения: 'экран-намерения',
  Кабинеты: 'экран-кабинеты',
  'Подключение кабинета': 'экран-подключение',
  'Счета и оплаты': 'экран-счета',
  /* Новый экран передачи 10.09.2026: раньше владелец приглашал людей
     вслепую — экрана не было вовсе. */
  'Участники и права': 'экран-участники',
  /* Второй раздел сервиса, передача 13.09.2026. */
  'Финансовая аналитика': 'экран-финансы',
  Поддержка: 'экран-поддержка',
  'Пароль и доступ': 'экран-пароль',
  Сборка: 'экран-сборка',
  'Добавить на телефон': 'экран-приложение',
  'Не найдено': 'экран-не-найдено',
  Вход: 'экран-вход',
  Приглашение: 'экран-приглашение',
} as const);

export type Подпись = keyof typeof ЭКРАНЫ;

/**
 * ═══════════════════════════════════════════════════════════════════════
 * НА КАКИХ ЭКРАНАХ СТОИТ «НАЗАД» — И КУДА ОНА ВЕДЁТ.
 *
 * Список взят из эталона: там `кнопка-назад` есть на запросах,
 * правилах, намерениях, кабинетах, подключении, пароле, сборке,
 * поддержке и счетах. Нет её на биддере (он и есть дом), на карточке
 * кампании (у неё своя шапка с возвратом) и на «не найдено».
 *
 * ПОДПИСЬ ВЕЗДЕ ОДНА — «биддер», кроме экрана запросов, где очевиден
 * другой родитель. Подписи эталона («Назад в счета и оплаты»
 * на поддержке) — это след стенда: он подставляет ПРЕДЫДУЩИЙ экран
 * своего списка, а не то место, откуда человек пришёл.
 * ═══════════════════════════════════════════════════════════════════════
 */
export const НАЗАД: Readonly<Partial<Record<Подпись, string>>> = Object.freeze({
  Запросы: 'кампанию',
  Намерения: 'биддер',
  Кабинеты: 'биддер',
  /* Единственный экран, где родитель очевиден и назван в эталоне:
     «Назад в кабинеты». Так и в самом эталоне. */
  'Подключение кабинета': 'кабинеты',
  'Счета и оплаты': 'биддер',
  'Участники и права': 'биддер',
  /* ═══════════════════════════════════════════════════════════════════
     У ФИНАНСОВОЙ АНАЛИТИКИ «НАЗАД» НЕТ — ЭТО НЕ ПОДРАЗДЕЛ БИДДЕРА.

     15.09.2026, владелец: «кнопка „назад в биддер" — это если у
     человека тариф на биддер и финансовую аналитику; а если только
     финансовая аналитика, тогда эта кнопка бессмысленная».

     Он прав, и довод даже шире тарифа: раздел продаётся отдельно
     и стоит в меню РЯДОМ с биддером, а не под ним. «Назад» из одного
     верхнего раздела в другой верхний — это не возврат, а переход,
     и называть его возвратом значит врать о устройстве кабинета.

     Уйти отсюда есть чем: раздел открывают из меню, и меню на месте.

     ЕСЛИ ПОЯВИТСЯ ПРИЗНАК «биддер куплен» — кнопку можно будет
     вернуть под условие. Сейчас такого признака во фронте нет
     вовсе, а показывать её всем ради половины — это та самая
     бессмысленная кнопка.
     ═══════════════════════════════════════════════════════════ */
  Поддержка: 'биддер',
  'Пароль и доступ': 'биддер',
  Сборка: 'биддер',
  'Добавить на телефон': 'биддер',
});

/** Куда ведёт «Назад» с этого экрана. `null` — кнопки нет вовсе. */
export function назадС(путь: string): string | null {
  return НАЗАД[подписьЭкрана(путь)] ?? null;
}


/**
 * Адрес → подпись экрана.
 *
 * ПОРЯДОК ВАЖЕН: `/rk/:ws/:cab/campaigns/:id` кончается на `campaigns`
 * плюс номер, а `/rk/:ws/:cab` — ничем; проверять надо от длинного
 * к короткому, иначе всё сойдётся на биддере.
 *
 * Хвост берётся ПОСЛЕДНИМ куском адреса, а не поиском подстроки:
 * подстрока `plan` встречается и в `/settings/plan`, и в имени
 * кабинета, если владелец назвал его «planeta».
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * КАК ЭКРАН НАЗВАН В ШАПКЕ — СЛОВАМИ ЭТАЛОНА, А НЕ КЛЮЧОМ.
 *
 * `подписьЭкрана` служит КЛЮЧОМ: по нему разложены коробка экрана,
 * столбик, кнопка «Назад», по нему же ходит сверка (`data-screen-label`)
 * и проба. Ключ переименовать нельзя, не переписав их все.
 *
 * А в шапке эталон пишет ДРУГИЕ слова — свой `screenTitle`:
 *
 *     clusters: «Поисковые запросы»   (у нас ключ «Запросы»)
 *     rules:    «Правила исключения»  (у нас ключ «Правила»)
 *
 * Разница видна только глазом: у надписи в шапке нет имени (`data-d`),
 * и сверка про неё молчит по устройству. Нашлось на снимке «рядом»
 * 26.08 — в шапке стояло «Запросы» при эталонных «Поисковые запросы».
 *
 * Экранов в карте ровно два: остальные семь эталон называет теми же
 * словами, что и мы.
 * ═══════════════════════════════════════════════════════════════════════
 */
const В_ШАПКЕ: Partial<Record<Подпись, string>> = {
  Запросы: 'Поисковые запросы',
};

/** Имя экрана так, как оно стоит в шапке кабинета. */
export function подписьВШапке(путь: string): string {
  const имя = подписьЭкрана(путь);
  return В_ШАПКЕ[имя] ?? имя;
}

export function подписьЭкрана(путь: string): Подпись {
  const куски = путь.split('/').filter(Boolean);
  const хвост = куски[куски.length - 1] ?? '';
  const предпоследний = куски[куски.length - 2] ?? '';

  if (куски[0] !== 'rk') return 'Не найдено';
  if (куски.length === 1) return 'Кабинеты'; // /rk — выбор пространства
  if (хвост === 'version') return 'Сборка';
  if (хвост === 'invite') return 'Приглашение';
  if (хвост === 'login' || хвост === 'register' || хвост === 'forgot' || хвост === 'reset' || хвост === 'verify') {
    return 'Вход';
  }
  if (предпоследний === 'campaigns') return 'Кампания';
  if (хвост === 'clusters') return 'Запросы';
  if (хвост === 'intents') return 'Намерения';
  /* ПОДКЛЮЧЕНИЕ — СВОЙ ЭКРАН, А НЕ «КАБИНЕТЫ». До 26.08 оба адреса
     назывались одним именем: у экрана подключения не было ни своего
     узла, ни своей коробки, и сверка мерила на нём чужие числа. */
  if (хвост === 'connect') return 'Подключение кабинета';
  if (хвост === 'cabinets') return 'Кабинеты';
  if (хвост === 'plan') return 'Счета и оплаты';
  if (хвост === 'support') return 'Поддержка';
  if (хвост === 'password' || хвост === 'settings') return 'Пароль и доступ';
  if (хвост === 'pwa') return 'Добавить на телефон';
  /* ═══════════════════════════════════════════════════════════════════
     «УЧАСТНИКИ» БОЛЬШЕ НЕ ЗОВУТСЯ «КАБИНЕТАМИ».

     До передачи 10.09.2026 экрана участников не существовало, а адрес
     `members` уже был — и назывался чужим именем, чтобы у него хоть
     какая-то коробка была. Теперь у него своё имя, своя коробка
     и свой замер.

     `journal` остаётся при «Кабинетах»: своего экрана у журнала
     в эталоне нет до сих пор.
     ═══════════════════════════════════════════════════════════════ */
  if (хвост === 'members') return 'Участники и права';
  if (хвост === 'fin') return 'Финансовая аналитика';
  if (хвост === 'journal') return 'Кабинеты';
  /**
   * `/rk/:ws` — выбор кабинета, `/rk/:ws/:cab` — биддер. Отличаются
   * только числом кусков: имена и того и другого — случайные строки.
   */
  if (куски.length === 2) return 'Кабинеты';
  /**
   * БИДДЕР — ЭТО РОВНО `/rk/:ws/:cab`, И НИЧТО ДЛИННЕЕ.
   *
   * Стояло `return 'Биддер'` без условия, и всякий неизвестный адрес
   * внутри кабинета — `/rk/w/c/чего-нет`, старая ссылка из закладки —
   * получал имя узла `экран-биддер`. На экране при этом рисовалось
   * «404 Такой страницы в кабинете нет»: рамка называла себя биддером,
   * содержимое говорило обратное, и сверка мерила на этом экране
   * числа чужого.
   *
   * Все известные хвосты кабинета названы выше поимённо, а карточка
   * кампании — по предпоследнему куску. Сюда доходит только то,
   * чего в кабинете нет.
   */
  if (куски.length === 3) return 'Биддер';
  return 'Не найдено';
}

/** Имя узла для текущего адреса. */
export function имяЭкрана(путь: string): string {
  return ЭКРАНЫ[подписьЭкрана(путь)];
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * КОРОБКА ЭКРАНА — ПОЛЯ И РАСКЛАДКА, ОДНИ НА ВЕСЬ КАБИНЕТ.
 *
 * Эталон держит поля страницы на самом узле `экран-*`, а не внутри
 * содержимого. У нас их не было вовсе: `<main>` шёл без полей,
 * а каждая страница отбивала свои — `p-3` тут, `padding: var(--sp-6)`
 * там, `px-5 py-4` в третьем месте. Тринадцать экранов, тринадцать
 * разных верхних отступов, и ни один не совпадал с замером.
 *
 * ТРИ ФОРМЫ, А НЕ ТРИНАДЦАТЬ. Замер говорит:
 *
 *   ▸ ПОЛОСА — биддер, кампания, запросы, правила, намерения:
 *     колонка во всю ширину, поля 20/20/96/20, зазор 16 (у биддера 20).
 *
 *   ▸ СТОЛБЕЦ ПОСЕРЕДИНЕ — счета, поддержка, пароль, сборка,
 *     кабинеты, вход: содержимое узкое и стоит по центру.
 *
 *   ▸ БЕЗ КОРОБКИ — приглашение: там своя рамка во весь экран.
 *
 * НИЖНЕЕ ПОЛЕ 96, И ЭТО НЕ ОПЕЧАТКА. Под содержимым кабинета живут
 * плавающие полосы — выделение строк, ответ действия, — и без запаса
 * последняя строка таблицы уходит под них.
 *
 * ЧИСЛА СВЕРЯЮТСЯ С ЗАМЕРОМ, а не выверяются глазами: сторож
 * `screen.spec.ts` читает `design/measured/коробки.json` — выжимку,
 * которую пишет `probe/design.mjs`, — и требует совпадения каждого
 * поля. Ошибиться в них нельзя молча.
 * ═══════════════════════════════════════════════════════════════════════
 */
export interface Коробка {
  display: 'flex' | 'block';
  flexDirection?: 'row' | 'column';
  justifyContent?: 'center';
  padding: string;
  gap?: string;
  /** Своя подложка. Есть у «Кабинетов», «Подключения» и «Входа». */
  background?: string;
}

const ПОЛОСА = (зазор: string): Коробка => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '20px 20px 96px',
  gap: зазор,
});

const СТОЛБЕЦ = (поля: string): Коробка => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  padding: поля,
});

export const КОРОБКИ: Readonly<Record<Подпись, Коробка>> = Object.freeze({
  Биддер: ПОЛОСА('20px'),
  Кампания: ПОЛОСА('16px'),
  Запросы: ПОЛОСА('16px'),
  Намерения: ПОЛОСА('16px'),
  'Счета и оплаты': СТОЛБЕЦ('32px 20px 96px'),
  /* Замер эталона 10.09.2026: те же поля, что у счетов и пароля —
     все три «спокойные» экраны пространства, и раскладка у них одна. */
  'Участники и права': СТОЛБЕЦ('32px 20px 96px'),
  /* ═══════════════════════════════════════════════════════════════════
     ФИНАНСЫ — ПОЛОСА, КАК БИДДЕР, И ЗАЗОР У НЕЁ 16.

     Числа снял замер эталона (`design/measured/коробки.json`, экран
     «Финансы»): поля 20/20/96/20, зазор 16. Столбцом её делать нельзя —
     таблица на двадцать четыре графы требует всей ширины полотна,
     а не восьмисот сорока точек по центру.
     ═══════════════════════════════════════════════════════════════ */
  'Финансовая аналитика': ПОЛОСА('16px'),
  Поддержка: СТОЛБЕЦ('32px 20px 96px'),
  'Пароль и доступ': СТОЛБЕЦ('32px 20px 96px'),
  Сборка: СТОЛБЕЦ('32px 20px 96px'),
  /* Поля из замера эталона: 32 сверху, 20 по бокам, 96 снизу —
     те же, что у счетов, поддержки, пароля и сборки. */
  'Добавить на телефон': СТОЛБЕЦ('32px 20px 96px'),
  /* ═════════════════════════════════════════════════════════════════
     У «КАБИНЕТОВ» СВОЯ ПОДЛОЖКА, И ОНА ЗАДАНА, А НЕ УНАСЛЕДОВАНА.

     В эталоне на этом экране `background: var(--bg-page)` стоит прямо
     на коробке — как и на «Подключении» и на «Входе». Остальные
     экраны прозрачны и берут подложку страницы.

     Разница не видна глазом (страница под ними того же цвета),
     но замер её видит: `rgb(245,246,248)` против `rgba(0,0,0,0)`.
     А видна она станет в тот день, когда у кабинета появится вторая
     подложка — например, тёмная тема с другим тоном страницы.
     ═════════════════════════════════════════════════════════════════ */
  Кабинеты: { ...СТОЛБЕЦ('40px 20px 48px'), background: 'var(--bg-page)' },
  /* Те же поля, что у «Кабинетов», и та же своя подложка: в эталоне
     это соседние экраны одного разговора. */
  'Подключение кабинета': { ...СТОЛБЕЦ('40px 20px 48px'), background: 'var(--bg-page)' },
  Вход: { ...СТОЛБЕЦ('32px 16px'), background: 'var(--bg-page)' },
  /* ═════════════════════════════════════════════════════════════════
     ЕДИНСТВЕННЫЙ ЭКРАН-КОЛОНКА С СОДЕРЖИМЫМ ПО ЦЕНТРУ.

     404 стоит посреди пустоты, и прижимать его к верху не к чему.
     Числа — с эталона дословно: `min-height:70vh; padding:var(--sp-12)
     var(--sp-5); align-items:center; justify-content:center;
     gap:var(--sp-3); text-align:center`.

     ВЫРАВНИВАНИЕ ПОПЕРЁК И ПО ТЕКСТУ ЗДЕСЬ НЕ ЛИШНЕЕ: без них
     колонка стоит по центру ПО ВЫСОТЕ, а число, заголовок и две
     кнопки жмутся к левому краю — то есть ровно наоборот тому,
     что нарисовано.

     ВЫСОТА 70vh — ЧТОБЫ 404 НЕ ПРИЛИПАЛ К ШАПКЕ. Рамка кабинета
     занимает столько, сколько занимает; на короткой странице
     `justify-content: center` без запаса высоты не центрирует
     ничего — центрировать нечего.
     ═════════════════════════════════════════════════════════════ */
  'Не найдено': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '70vh',
    padding: '48px 20px',
    gap: '12px',
    textAlign: 'center',
  },
  /* Приглашение рисует свою рамку во весь экран — коробки ему не надо. */
  Приглашение: { display: 'block', padding: '0px' },
});

export function коробкаЭкрана(путь: string): Коробка {
  return коробкаПодписи(подписьЭкрана(путь));
}

/**
 * Коробка по подписи, а не по адресу.
 *
 * Нужна ровно одному месту — экрану «не найдено» ВНЕ рамки кабинета
 * (`<Route path="*">` верхнего уровня, до входа). Там рамки нет,
 * коробку ставит сам экран, а спрашивать её по адресу нельзя:
 * адрес там какой угодно, и `/rk/чего-нет` разобрался бы
 * в «Кабинеты».
 */
export function коробкаПодписи(подпись: Подпись): Коробка {
  return КОРОБКИ[подпись];
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СТОЛБИК ЭКРАНА — ОДНА КОЛОНКА ПОСЕРЕДИНЕ, И «НАЗАД» ВНУТРИ НЕЁ.
 *
 * Шесть экранов кабинета устроены в эталоне одинаково: `<main>` —
 * ряд с выравниванием по центру, и в нём РОВНО ОДИН ребёнок —
 * колонка своей ширины. Первым в этой колонке стоит «Назад».
 *
 * У нас «Назад» рисовала рамка, ПЕРВЫМ РЕБЁНКОМ САМОГО `<main>` —
 * то есть вторым элементом ряда, рядом с содержимым, а не над ним.
 * Последствия видны замером:
 *
 *   ▸ на 1440 колонка съезжает вправо на половину ширины кнопки:
 *     `justify-content: center` центрирует ПАРУ, а не содержимое;
 *
 *   ▸ на 390 места на двоих нет, и оба сжимаются. Сверка 26.08:
 *     «На телефон» — `шаги-приложения` 348 против 275,9,
 *     `подсказка-айфон` 306 против 233,9. Ровно семьдесят два
 *     пикселя, отобранных кнопкой у содержимого.
 *
 * ЧИСЛА ВЗЯТЫ ИЗ ЭТАЛОНА, а не подобраны: 840 у счетов, поддержки
 * и кабинетов, 760 у подключения, 640 у «на телефон», 520 у сборки,
 * 460 у пароля. Зазор тоже свой у каждого: `--sp-5` там, где блоки
 * крупные, `--sp-4` там, где их много.
 *
 * У ЭКРАНОВ-ПОЛОС СТОЛБИКА НЕТ ВОВСЕ (`null`): биддер, кампания,
 * запросы, правила и намерения — колонка во всю ширину, и «Назад»
 * там и так первый ребёнок `<main>`, как в эталоне.
 * ═══════════════════════════════════════════════════════════════════════
 */
export interface Столбик {
  maxWidth: number;
  gap: string;
}

export const СТОЛБИКИ: Readonly<Partial<Record<Подпись, Столбик>>> = Object.freeze({
  Кабинеты: { maxWidth: 840, gap: 'var(--sp-5)' },
  'Подключение кабинета': { maxWidth: 760, gap: 'var(--sp-5)' },
  'Добавить на телефон': { maxWidth: 640, gap: 'var(--sp-5)' },
  'Счета и оплаты': { maxWidth: 840, gap: 'var(--sp-4)' },
  /* ═══════════════════════════════════════════════════════════════════
     СТОЛБЕЦ УЧАСТНИКОВ — 840, КАК В ЭТАЛОНЕ.

     Здесь его НЕ БЫЛО ВОВСЕ, и экран собирался без столбика: карточка
     владельца выходила 219 точек вместо 840, список — 280, форма
     приглашения — 388. Каждая панель сжималась по своему содержимому,
     и экран читался лесенкой обрывков.

     Поймала это проба `probe/kabinet.mjs` в тот же час, когда экран
     завели в её список состояний, — глазами на снимке 1440 разница
     тоже видна, но объяснить её было бы нечем.
     ═══════════════════════════════════════════════════════════════ */
  'Участники и права': { maxWidth: 840, gap: 'var(--sp-4)' },
  Поддержка: { maxWidth: 840, gap: 'var(--sp-4)' },
  'Пароль и доступ': { maxWidth: 460, gap: 'var(--sp-4)' },
  Сборка: { maxWidth: 520, gap: 'var(--sp-4)' },
  Вход: { maxWidth: 380, gap: 'var(--sp-5)' },
});

/** Столбик текущего экрана. `null` — экран-полоса, столбика нет. */
export function столбикЭкрана(путь: string): Столбик | null {
  return СТОЛБИКИ[подписьЭкрана(путь)] ?? null;
}
